import { useState, useEffect, useRef } from 'react'

import {
  ActionIcon,
  Checkbox,
  Divider,
  Loader,
  MultiSelect,
  Select,
  TextInput,
  Tooltip,
} from '@mantine/core'

import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { toast } from 'src/components/Overlays/Toast/Toast'
import DNButton from 'src/components/Buttons/Button/DNButton'
import {
  GET_DOCUMENT_TEMPLATE,
  UPDATE_DOCUMENT_TEMPLATE,
} from 'src/graphql/documentTemplates.graphql'
import {
  GET_DOCUMENT_TEMPLATE_FIELDS,
  UPDATE_DOCUMENT_TEMPLATE_FIELD,
  REFRESH_DOCUMENT_TEMPLATE_FIELDS,
} from 'src/graphql/documentTemplateFields.graphql'
import IconLeftArrow from 'src/icons/IconLeftArrow'
import IconCopy from 'src/icons/IconCopy'
import DnTable from 'src/components/Core/DnTable'

import SettingsField from '../../components/SettingsField'

const DEBOUNCE_DELAY = 500

const signerOptions = [
  { value: 'APPLICANT', label: 'Applicant' },
  { value: 'RECRUITER', label: 'Recruiter' },
]

const fieldTypeOptions = [
  { value: 'SHORT_TEXT', label: 'Short Text' },
  { value: 'CHECKBOX', label: 'Checkbox' },
  { value: 'RADIO_GROUP', label: 'Radio Group' },
  { value: 'INTEGER', label: 'Integer' },
  { value: 'DOLLAR', label: 'Dollar' },
  { value: 'DATE', label: 'Date' },
  { value: 'SIGNATURE', label: 'Signature' },
  { value: 'SIGNATURE_DATE', label: 'Signature Date' },
  { value: 'INITIAL', label: 'Initial' },
]

const DocumentTemplateDetailsPage = ({
  documentTemplateId,
  setSelectedTemplateId,
  refetchPackage,
}) => {
  const { currentUser } = useAuth()
  const [templateName, setTemplateName] = useState<string | undefined>(undefined)
  const [fields, setFields] = useState([])
  const [initialLoad, setInitialLoad] = useState(true)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const { data: templateData } = useQuery(GET_DOCUMENT_TEMPLATE, {
    variables: { documentTemplateId },
    onCompleted: (data) => {
      setTemplateName(data.documentTemplate.documentName)
    },
  })

  const {
    data: fieldsData,
    loading: fieldsLoading,
    refetch,
  } = useQuery(GET_DOCUMENT_TEMPLATE_FIELDS, {
    variables: { documentTemplateId },
    onCompleted: (data) => {
      setFields(data.documentTemplateFields)
      setInitialLoad(false)
    },
  })

  const [updateTemplate] = useMutation(UPDATE_DOCUMENT_TEMPLATE, {
    onError: (error) => {
      // Try multiple paths to support both dev and production environments
      const errorMessage =
        error.graphQLErrors?.[0]?.extensions?.originalError?.message ||
        error.graphQLErrors?.[0]?.message ||
        error.message ||
        'Failed to update template'
      toast(errorMessage, 'error')
    },
    onCompleted: () => toast('Template name updated', 'success'),
  })

  const [updateField] = useMutation(UPDATE_DOCUMENT_TEMPLATE_FIELD, {
    onError: () => toast('Unable to update field', 'error'),
    onCompleted: () => toast('Field updated', 'success'),
  })

  const [refreshFields, { loading: refreshLoading }] = useMutation(
    REFRESH_DOCUMENT_TEMPLATE_FIELDS,
    {
      onError: (error) =>
        toast(error?.message || 'Unable to refresh document fields', 'error', {
          documentTemplateId,
          userId: currentUser?.id,
          operation: 'refreshDocumentTemplateFields',
        }),
      onCompleted: ({ refreshDocumentFields: updatedFields }) => {
        setFields(updatedFields)
        toast('Document fields refreshed', 'success')
      },
    }
  )

  const handleRefreshFields = () => {
    const fieldId = fields[0]?.documentTemplateFieldId

    if (!fieldId) {
      toast('No document fields available to refresh', 'error')
      return
    }

    refreshFields({
      variables: { documentTemplateFieldId: fieldId },
    })
  }

  useEffect(() => {
    if (
      !templateData ||
      templateName === templateData.documentTemplate.documentName ||
      templateName === undefined
    )
      return

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      updateTemplate({
        variables: {
          documentTemplateId,
          input: { documentName: templateName },
        },
      })
      refetchPackage()
    }, DEBOUNCE_DELAY)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateName, templateData])

  const handleFieldChange = (id, field, value) => {
    setFields((prev) =>
      prev.map((f) =>
        f.documentTemplateFieldId === id ? { ...f, [field]: value } : f
      )
    )
  }

  const handleFieldUpdate = (id, field, value) => {
    updateField({
      variables: {
        documentTemplateFieldId: id,
        input: { [field]: value },
      },
    })
  }

  const columns = [
    {
      key: 'id',
      width: 8,
      label: 'ID',
      render: (field) => (
        <Tooltip label={field.documentTemplateFieldId}>
          <ActionIcon
            variant="subtle"
            size={14}
            color="gray"
            onClick={() => {
              navigator.clipboard.writeText(field.documentTemplateFieldId)
              toast('Copied to clipboard', 'success')
            }}
          >
            <IconCopy className="fill-none stroke-doubleNickel-gray-700" />
          </ActionIcon>
        </Tooltip>
      ),
    },
    {
      key: 'fieldLabel',
      width: 25,
      label: 'Field Label',
      render: (field) => (
        <TextInput
          variant="unstyled"
          size="xs"
          value={field.fieldLabel || ''}
          onChange={(e) =>
            handleFieldChange(
              field.documentTemplateFieldId,
              'fieldLabel',
              e.target.value
            )
          }
          onBlur={(e) =>
            handleFieldUpdate(
              field.documentTemplateFieldId,
              'fieldLabel',
              e.target.value
            )
          }
        />
      ),
    },
    {
      key: 'fieldName',
      width: 17,
      label: 'Field Name',
      render: (field) => (
        <TextInput
          variant="unstyled"
          size="xs"
          value={field.fieldName}
          onChange={(e) =>
            handleFieldChange(
              field.documentTemplateFieldId,
              'fieldName',
              e.target.value
            )
          }
          onBlur={(e) =>
            handleFieldUpdate(
              field.documentTemplateFieldId,
              'fieldName',
              e.target.value
            )
          }
        />
      ),
    },
    {
      key: 'fieldType',
      width: 17,
      label: 'Field Type',
      render: (field) => (
        <Select
          variant="unstyled"
          size="xs"
          data={fieldTypeOptions}
          value={field.fieldType}
          onChange={(value) => {
            handleFieldChange(
              field.documentTemplateFieldId,
              'fieldType',
              value
            )
            handleFieldUpdate(
              field.documentTemplateFieldId,
              'fieldType',
              value
            )
          }}
        />
      ),
    },
    {
      key: 'signers',
      width: 25,
      label: 'Signers',
      render: (field) => (
        <MultiSelect
          variant="unstyled"
          size="xs"
          data={signerOptions}
          value={field.signers}
          onChange={(value) => {
            handleFieldChange(
              field.documentTemplateFieldId,
              'signers',
              value
            )
            handleFieldUpdate(
              field.documentTemplateFieldId,
              'signers',
              value
            )
          }}
        />
      ),
    },
    {
      key: 'isRequired',
      width: 8,
      label: 'Is Required',
      render: (field) => (
        <div className="flex justify-center">
          <Checkbox
            checked={field.isRequired}
            onChange={(e) => {
              handleFieldChange(
                field.documentTemplateFieldId,
                'isRequired',
                e.currentTarget.checked
              )
              handleFieldUpdate(
                field.documentTemplateFieldId,
                'isRequired',
                e.currentTarget.checked
              )
            }}
          />
        </div>
      ),
    },
  ]

  if (initialLoad || fieldsLoading)
    return (
      <div className="flex h-full items-center justify-center">
        <Loader size="xs" color="gray" />
      </div>
    )

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto">
      <div className="flex flex-row items-center justify-between">
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => {
            setSelectedTemplateId(null)
            refetch()
            refetchPackage()
          }}
        >
          <IconLeftArrow className="fill-none stroke-doubleNickel-gray-700" />
        </ActionIcon>
      </div>
      <Divider />
      <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
        <SettingsField title="Template Name" description="Name of the template.">
          <div className="flex items-center gap-2">
            <TextInput
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              size="xs"
            />
          </div>
        </SettingsField>
        <SettingsField
          title="Document Fields"
          description={`${fields.length} document fields`}
        >
          <DNButton
            onClick={handleRefreshFields}
            loading={refreshLoading}
            disabled={!fields.length || refreshLoading}
          >
            Refresh
          </DNButton>
        </SettingsField>
        <DnTable
          enableColumnResize={true}
          enablePagination={false}
          columns={columns}
          data={fields}
        />
      </div>
    </div>
  )
}

export default DocumentTemplateDetailsPage
